import { FormattedMessage, useIntl } from 'react-intl';
import { Form, Select, Row, Col, Button, Tooltip } from 'antd';

import { useEffect } from 'react';
import FloatingFormItem from '@/components/_common/FloatingFormItem';

import { useDefaultEmptyFieldError } from '@/hooks';
import CustomersSelectField from '@/components/_common/CustomersSelectField';
import {
  AccountReceivableDebtor,
  ListAccountReceivableForGroupingRequest,
} from '@/core/domain/financial/AccountReceivable/AccountReceivable.types';
import { CompaniesInfiniteSelectField } from '@/components/_common';
import InsurerInfiniteSelectField from '@/components/_common/InsurerInfiniteSelectField';

interface Props {
  onFilter: (filter: ListAccountReceivableForGroupingRequest) => void;
  filter?: ListAccountReceivableForGroupingRequest;
  loading?: boolean;
}

function AccountReceivableGroupingFilter({ onFilter, filter, loading }: Props) {
  const [form] = Form.useForm<ListAccountReceivableForGroupingRequest>();
  const intl = useIntl();
  const defaultEmptyFieldError = useDefaultEmptyFieldError();
  const debtor = Form.useWatch('debtor', form);

  useEffect(() => filter && form.setFieldsValue(filter), [filter]);

  async function handleSubmit() {
    try {
      const values = await form.validateFields();
      onFilter(values);
    } catch {}
  }

  useEffect(() => {
    if (filter) {
      form.setFieldsValue(filter);
    }
  }, []);

  return (
    <>
      <Form layout="vertical" form={form}>
        <Row gutter={[16, 9]}>
          <Col flex={1}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'groupings.form.company',
              })}
              name="companyId"
              rules={[defaultEmptyFieldError]}
            >
              <CompaniesInfiniteSelectField />
            </FloatingFormItem>
          </Col>

          <Col flex={1}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'groupings.form.debtor',
              })}
              name="debtor"
              rules={[defaultEmptyFieldError]}
            >
              <Select
                allowClear
                style={{ width: '100' }}
                options={[
                  {
                    value: AccountReceivableDebtor.Costumer,

                    label: intl.formatMessage({
                      id: 'groupings.form.customer',
                    }),
                  },
                  {
                    value: AccountReceivableDebtor.Insurer,

                    label: intl.formatMessage({
                      id: 'groupings.form.insurer',
                    }),
                  },
                ]}
              />
            </FloatingFormItem>
          </Col>
          {debtor === AccountReceivableDebtor.Costumer ? (
            <Col xs={24} sm={24} md={6}>
              <FloatingFormItem
                label={intl.formatMessage({
                  id: 'groupings.form.customer',
                })}
                name="customerId"
                rules={[defaultEmptyFieldError]}
              >
                <CustomersSelectField />
              </FloatingFormItem>
            </Col>
          ) : (
            <Col xs={24} sm={24} md={6}>
              <FloatingFormItem
                label={intl.formatMessage({
                  id: 'groupings.form.insurer',
                })}
                name="insurerId"
                rules={[defaultEmptyFieldError]}
              >
                <InsurerInfiniteSelectField allowClear />
              </FloatingFormItem>
            </Col>
          )}

          <Col flex={1} lg={5}>
            <Form.Item<ListAccountReceivableForGroupingRequest>
              shouldUpdate
              noStyle
            >
              {({ getFieldsValue }) => {
                const values = getFieldsValue();
                const isDisabled =
                  !values.companyId ||
                  !values.debtor ||
                  (values.debtor === AccountReceivableDebtor.Costumer
                    ? !values.customerId
                    : !values.insurerId);

                return (
                  <Tooltip
                    title={isDisabled ? 'Selecione os campos' : undefined}
                  >
                    <Button
                      size="large"
                      type="primary"
                      onClick={handleSubmit}
                      disabled={isDisabled}
                      loading={loading}
                      block
                    >
                      <FormattedMessage id="groupings.generate.searchAccounts" />
                    </Button>
                  </Tooltip>
                );
              }}
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </>
  );
}

export default AccountReceivableGroupingFilter;
